/**
 * The hour axis a depth chart spans.
 *
 * The counterpart of `dayAxis` for buckets that are already one site-local hour. A missing hour
 * has to exist on the axis, or the hours either side of it are drawn adjacent and the line
 * reads as a measured trend across a gap. Keys are `YYYY-MM-DD HH:mm:ss`.
 */

const MS_PER_HOUR = 60 * 60 * 1000;
/** An hourly depth request is capped at 31 days, so a wider hole is not a gap worth filling. */
const MAX_FILLED_GAP_HOURS = 31 * 24;

const HOUR_KEY = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

function uniqueSortedHours<TRow>(rows: TRow[], hour: (row: TRow) => string): string[] {
    const unique = new Set<string>();
    for (const row of rows) {
        const value = hour(row);
        if (value) unique.add(value);
    }
    return Array.from(unique).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/** The key as a UTC timestamp of its wall-clock components, so adding an hour never skips one. */
function toHourTimestamp(key: string): number | null {
    const match = HOUR_KEY.exec(key);
    if (!match) return null;
    const hour = Number(match[4]);
    if (hour > 23) return null;
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), hour);
}

function toHourKey(timestamp: number): string {
    const date = new Date(timestamp);
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:00:00`;
}

/**
 * Every hour from the first reading to the last, with the unmeasured ones present as gaps.
 *
 * A hole wider than {@link MAX_FILLED_GAP_HOURS} is left as it is, for the same reason a day
 * axis refuses to fill a multi-year hole: a drifted clock would hang the chart.
 */
export function hourAxis<TRow>(rows: TRow[], hour: (row: TRow) => string): string[] {
    const keys = uniqueSortedHours(rows, hour);
    const filled: string[] = [];

    for (let index = 0; index < keys.length; index++) {
        const key = keys[index];
        const nextKey = keys[index + 1];
        filled.push(key);
        if (nextKey === undefined) continue;

        const from = toHourTimestamp(key);
        const to = toHourTimestamp(nextKey);
        if (from === null || to === null || to - from > MAX_FILLED_GAP_HOURS * MS_PER_HOUR) {
            continue;
        }
        for (let timestamp = from + MS_PER_HOUR; timestamp < to; timestamp += MS_PER_HOUR) {
            filled.push(toHourKey(timestamp));
        }
    }

    return filled;
}
